import { z } from 'zod';

export const TENNIS_ACTIVITY_ID = 'tennis';
export const TENNIS_EVENTS = {
  JOIN: 'tennis:join',
  LEAVE: 'tennis:leave',
  ACTION: 'tennis:action',
  STATE: 'tennis:state',
  FRAME: 'tennis:frame',
  INPUT: 'tennis:input',
  CAPTION: 'tennis:caption',
} as const;
const finite = (min: number, max: number) => z.number().finite().min(min).max(max);
export const tennisSetupSchema = z.object({
  gamesToWin: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
  difficulty: z.enum(['casual', 'pro']).default('casual'),
});
export type TennisSetup = z.infer<typeof tennisSetupSchema>;
export const tennisInputSchema = z.object({
  type: z.enum(['swing', 'toss']),
  shotType: z.enum(['topspin', 'slice', 'lob', 'smash']),
  speed: finite(0.5, 2),
  dirX: finite(-1, 1),
  dirY: finite(-1, 1),
  // Camera strokes use timing at the authoritative simulation. Touch swipes without camera tracking
  // use `swipe`: the swipe's sideways direction picks the side on every timing. Other controls are manual.
  aimMode: z.enum(['manual', 'timing', 'swipe']).optional(),
});
export type TennisInput = z.infer<typeof tennisInputSchema>;
const scoreSchema = z.object({
  playerPoints: finite(0, 10000).int(),
  opponentPoints: finite(0, 10000).int(),
  playerGames: finite(0, 3).int(),
  opponentGames: finite(0, 3).int(),
  server: z.enum(['player', 'opponent']),
  deuceSide: z.boolean(),
});
export type TennisScore = z.infer<typeof scoreSchema>;
export const initialTennisScore = (): TennisScore => ({
  playerPoints: 0,
  opponentPoints: 0,
  playerGames: 0,
  opponentGames: 0,
  server: 'player',
  deuceSide: true,
});
export function advanceTennisScore(score: TennisScore, winner: 0 | 1): TennisScore {
  const next = { ...score, deuceSide: !score.deuceSide };
  if (winner === 0) next.playerPoints++;
  else next.opponentPoints++;
  if (
    Math.max(next.playerPoints, next.opponentPoints) >= 4 &&
    Math.abs(next.playerPoints - next.opponentPoints) >= 2
  ) {
    if (next.playerPoints > next.opponentPoints) next.playerGames++;
    else next.opponentGames++;
    next.playerPoints = next.opponentPoints = 0;
    next.deuceSide = true;
    next.server = next.server === 'player' ? 'opponent' : 'player';
  }
  return next;
}
export function tennisScoreText(s: TennisScore): string {
  const p = s.playerPoints,
    o = s.opponentPoints;
  if (p >= 3 && o >= 3)
    return p === o ? 'Deuce' : `Advantage ${p > o ? 'player one' : 'player two'}`;
  const labels = ['Love', '15', '30', '40'];
  return `${labels[Math.min(p, 3)]} – ${labels[Math.min(o, 3)]}`;
}
const actor = z.object({
  px: finite(-50, 50),
  pz: finite(-50, 50),
  tx: finite(-50, 50),
  tz: finite(-50, 50),
  st: z.string().max(20),
  sw: finite(-1, 10),
  av: finite(-1, 10),
});
/** A court sound at the moment the host physics produced it. `n` increases for a host's lifetime. */
export const tennisSfxSchema = z.object({
  n: finite(1, 1e9).int(),
  k: z.enum(['hit', 'bounce', 'net', 'squeak']),
  /** Strength, 0 (soft) to 2 (power smash). */
  v: finite(0, 2),
  /** Hits only: the stroke, which shapes the contact tone. */
  s: z.enum(['topspin', 'slice', 'lob', 'smash']).optional(),
  x: finite(-100, 100),
  y: finite(-30, 100),
  z: finite(-100, 100),
});
export type TennisSfx = z.infer<typeof tennisSfxSchema>;
export const tennisFrameSchema = z.object({
  phase: z.enum([
    'TITLE',
    'INTRO',
    'SERVE_STANCE',
    'SERVE_TOSS',
    'RALLY',
    'POINT_OVER',
    'REPLAY',
    'MATCH_OVER',
  ]),
  phaseTimer: finite(0, 100000),
  cameraMode: z.enum(['INTRO_PAN', 'SERVE_CAM', 'RALLY_CAM', 'REPLAY_CAM']),
  score: scoreSchema,
  ball: z.object({
    px: finite(-100, 100),
    py: finite(-30, 100),
    pz: finite(-100, 100),
    vx: finite(-200, 200),
    vy: finite(-200, 200),
    vz: finite(-200, 200),
    inPlay: z.boolean(),
    lastHitter: z.enum(['player', 'opponent']),
    shotType: z.enum(['TOPSPIN', 'SLICE', 'LOB', 'SMASH', 'FLAT', 'POWER']),
    isPowerShot: z.boolean(),
    rallyStrokeCount: finite(0, 10000).int(),
  }),
  actors: z.object({
    player: actor,
    playerPartner: actor,
    opponent: actor,
    opponentPartner: actor,
  }),
  banner: z
    .object({
      seq: finite(0, 100000).int(),
      text: z.string().max(80),
      style: z.string().max(30),
      subText: z.string().max(80),
    })
    .nullable(),
  // The last half second of court sounds, so a client that only sees snapshots still hears them
  // when a volatile frame is dropped. Optional: older hosts send none.
  sfx: z.array(tennisSfxSchema).max(16).optional(),
});
export type TennisFrame = z.infer<typeof tennisFrameSchema>;
export const tennisPointSchema = z.object({
  winner: z.union([z.literal(0), z.literal(1)]),
  reason: z.enum(['', 'Out', 'Net', 'Point', 'Nice Shot!', 'Double Bounce', 'Double Fault']),
  strokes: finite(0, 10000).int(),
  shot: z.enum(['TOPSPIN', 'SLICE', 'LOB', 'SMASH', 'FLAT', 'POWER']),
});
export type TennisPoint = z.infer<typeof tennisPointSchema>;
export const tennisActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('play') }),
  z.object({ type: z.literal('watch') }),
  z.object({ type: z.literal('start') }),
  z.object({ type: z.literal('reset') }),
  // The host switches the companion's play-by-play on or off, at any point in the activity.
  z.object({ type: z.literal('commentary'), on: z.boolean() }),
  z.object({
    type: z.literal('input'),
    matchId: z.string().max(80),
    seq: finite(1, 1e9).int(),
    input: tennisInputSchema,
  }),
  z.object({
    type: z.literal('frame'),
    matchId: z.string().max(80),
    seq: finite(1, 1e9).int(),
    frame: tennisFrameSchema,
  }),
  z.object({
    type: z.literal('point'),
    matchId: z.string().max(80),
    seq: finite(1, 1e9).int(),
    point: tennisPointSchema,
  }),
]);
export type TennisAction = z.infer<typeof tennisActionSchema>;
export interface TennisPlayer {
  sessionId: string;
  name: string;
}
export interface TennisRoomState {
  roomId: string;
  revision: number;
  phase: 'lobby' | 'playing' | 'finished';
  setup: TennisSetup;
  players: TennisPlayer[];
  matchId: string | null;
  authority: string | null;
  score: TennisScore;
  pointSeq: number;
  frameSeq: number;
  frame: TennisFrame | null;
  notice: string | null;
  /** The companion calls each point aloud. The host switches it; on by default. */
  commentary: boolean;
}
